// PieChart.js
// Renders the chart it is given. Saved display settings (title, axes, colours)
// are applied by the caller — applyChartSettings, with the settings of the
// version the shown run executed — so this component reads nothing itself.
//
// The figure (traces + layout) comes from a pure builder so the combined
// chart's whole-image export can draw this chart without rendering it.
import React from 'react';
import Plot from 'react-plotly.js';
import { useTheme } from '@mui/material/styles';
import ChartCard from './ChartCard';
import ChartExportButton from './ChartExportButton';
import { downloadChartPng, exportTitle } from '../../utils/chartExport';
import {
  CHART_CONFIG,
  CHART_SURFACE_RING,
  buildLayout,
  seriesColor,
} from '../../utils/chartTheme';

export const buildPieFigure = (chartData, { ink, themePalette }) => {
  // A pie's slices ARE its series, so they take the palette in order, one slice
  // per slot; a slice past the last slot takes that slot's tinted lap colour.
  const palette = chartData.colors || themePalette;
  const showLegend = chartData.show_legend !== false;

  const data = [{
    type: 'pie',
    labels: chartData.elements.map(el => el.label),
    values: chartData.elements.map(el => el.angle),
    marker: {
      colors: chartData.elements.map((_, index) => seriesColor(palette, index)),
      // A hairline of the card between slices, so two adjacent fills read as
      // two slices rather than one shape that changes colour.
      line: { color: CHART_SURFACE_RING, width: 2 }
    },
    textposition: 'auto',
    textinfo: 'label+percent',
    // A label too wide for its slice is drawn outside the pie. The margins
    // below leave almost no room above and below it, so without this a label
    // near the top or bottom of the pie ran past the card's edge and was cut
    // in half. With it, plotly widens the margins to fit and shrinks the pie.
    automargin: true,
    hoverinfo: 'label+value+percent'
  }];

  // A pie has no axes, so no tick label grows a margin here; only the slice
  // labels drawn outside the pie do (automargin, above). Otherwise the slices
  // get the whole card apart from the legend's band.
  const layout = buildLayout({
    showLegend,
    ink,
    extraMargin: { l: 8, r: 8, b: 8 }
  });

  return { data, layout };
};

// `showTitle: false` is for a frame that prints the title itself (a View
// card); the export still carries it. `ancestorTitles` are the titles of the
// combined charts this one sits inside, printed on the export as a prefix.
function PieChartComponent({ chartData, showTitle = true, ancestorTitles = [] }) {
  const theme = useTheme();
  // The series palette and the reading ink both come from the app theme, so the
  // chart layer holds no colour of its own.
  const ink = { primary: theme.palette.text.primary, secondary: theme.palette.text.secondary };
  const { data, layout } = buildPieFigure(chartData, { ink, themePalette: theme.palette.custom.chartColors });
  const title = exportTitle(ancestorTitles, chartData.title);

  return (
    <ChartCard
      title={showTitle ? chartData.title : null}
      ink={ink}
      actions={<ChartExportButton onExport={() => downloadChartPng({ data, layout, title })} />}
    >
      <Plot
        data={data}
        layout={layout}
        config={CHART_CONFIG}
        style={{ width: '100%', height: 'calc(100% - 50px)' }}
        useResizeHandler={true}
      />
    </ChartCard>
  );
}

export default PieChartComponent;
